import { useState, FormEvent } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d50q9eiopjgqftil4hg1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#FAF8F5] px-4 py-8">
      <div className="bg-[#0F1419] rounded-[32px] px-16 py-20 relative overflow-hidden">
        {/* Background Watermark */}
        <div className="absolute bottom-0 left-0 right-0 text-[180px] font-bold text-[#1A1E24] opacity-15 leading-none pointer-events-none whitespace-nowrap">
          MELINDA HOLCOMB
        </div>

        <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Column 1 - Brand CTA */}
          <div className="md:col-span-1">
            <h3 className="font-serif text-5xl text-white leading-tight mb-6">
              Join My<br />
              Reading<br />
              Community
            </h3>
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Navigate</h4>
            <ul className="space-y-4">
              <li>
                <a href="#home" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  Home
                </a>
              </li>
              <li>
                <a href="#books" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  Books
                </a>
              </li>
              <li>
                <a href="#about" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  About
                </a>
              </li>
              <li>
                <a href="#blog" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  Blog
                </a>
              </li>
              <li>
                <a href="#contact" className="text-sm text-gray-400 hover:text-white hover:underline transition-colors cursor-pointer">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3 - Social Connect */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Follow</h4>
            <ul className="space-y-4">
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-gray-400 hover:text-white transition-colors cursor-pointer group">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-instagram-line text-xl group-hover:scale-110 transition-transform"></i>
                  </div>
                  <span>Instagram</span>
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-gray-400 hover:text-white transition-colors cursor-pointer group">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-twitter-x-line text-xl group-hover:scale-110 transition-transform"></i>
                  </div>
                  <span>Twitter</span>
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-sm text-gray-400 hover:text-white transition-colors cursor-pointer group">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-book-2-line text-xl group-hover:scale-110 transition-transform"></i>
                  </div>
                  <span>Goodreads</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 - Newsletter */}
          <div>
            <p className="text-base text-white mb-6 leading-relaxed">
              Subscribe to receive<br />
              exclusive updates
            </p>
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="flex items-center gap-0">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                required
                className="flex-1 h-12 bg-[#1A1D23] border border-gray-700 rounded-l-full px-5 text-white placeholder-gray-500 focus:outline-none focus:border-teal-600 transition-colors text-sm"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:bg-teal-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed -ml-px"
              >
                <i className="ri-arrow-right-line text-gray-900 text-xl"></i>
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-teal-400 mt-2">Thank you for subscribing!</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-400 mt-2">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div className="relative z-10 mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            © 2024 Melinda Holcomb. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
